import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, KeyRound, Undo2 } from "lucide-react";
import FittinMark from "@/components/fittin/FittinMark";
import { PassQR } from "@/components/fittin/FitKeyPass";
import { ConfirmFitForm, ReturnForm } from "@/components/fittin/FitKeyActions";
import { fetchPublicFitKey, isPreviewId, loadLocalFitKey, passSize } from "@/lib/fitkey";
import { startSession, useSession } from "@/lib/session";

// /k/:fitkeyId — the page the Wallet pass links to:
//   https://getfittin.com/k/<fitkey_id>#<access_key>
// The fragment (never sent to a server) is exchanged for a session token, then
// removed from the address bar. Signed in: confirm fit + request a return.
// Not signed in: the public pass summary and a prompt to sign in.
export default function FitKeyPage() {
  const { fitkeyId } = useParams();
  const session = useSession();
  const preview = isPreviewId(fitkeyId);

  const [linkState, setLinkState] = useState("idle"); // idle | signing | failed
  const handled = useRef(false);

  // 1) Sign in with the key from the pass link, then strip the fragment.
  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    // index.html moves the key out of the URL before analytics loads; the hash
    // is only a fallback (e.g. client-side navigation to a pass link).
    const raw = window.__fittinPassKey || (window.location.hash || "").replace(/^#/, "");
    window.__fittinPassKey = undefined;
    if (window.location.hash) {
      window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
    }
    let accessKey = raw;
    try {
      accessKey = decodeURIComponent(raw);
    } catch (_e) {
      /* keep raw */
    }
    if (!accessKey || preview) return;
    setLinkState("signing");
    startSession({ fitkey_id: fitkeyId, access_key: accessKey }).then((r) =>
      setLinkState(r.ok ? "idle" : "failed")
    );
  }, [fitkeyId, preview]);

  // 2) Public pass summary.
  const [summary, setSummary] = useState(null);
  const [loadState, setLoadState] = useState("loading"); // loading | ok | notfound | error
  useEffect(() => {
    let alive = true;
    if (preview) {
      const local = loadLocalFitKey(fitkeyId);
      setSummary(local ? { ...local, reference_brand: local.brand_name || local.reference_brand } : null);
      setLoadState(local ? "ok" : "notfound");
      return undefined;
    }
    setLoadState("loading");
    fetchPublicFitKey(fitkeyId).then((r) => {
      if (!alive) return;
      if (r.ok) {
        setSummary(r.data);
        setLoadState("ok");
      } else {
        setLoadState(r.status === 404 ? "notfound" : "error");
      }
    });
    return () => {
      alive = false;
    };
  }, [fitkeyId, preview]);

  const signedHere = !!session && session.fitkey_id === fitkeyId;
  const size = useMemo(() => (summary ? passSize(summary) : { unit: "EU", value: "—" }), [summary]);
  const brand = summary?.reference_brand || "";
  const model = summary?.reference_model || "";
  const pair = [brand, model].filter(Boolean).join(" ");
  const sizeRecommended = size.value && size.value !== "—" ? `${size.unit} ${size.value}` : undefined;

  return (
    <main data-testid="fitkey-page" className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
      <header className="mx-auto max-w-xl px-5 h-16 flex items-center justify-between">
        <Link to="/" data-testid="fitkey-page-home" className="flex items-center gap-2">
          <FittinMark size={24} />
          <span className="text-[16px] font-medium tracking-tight">Fittin</span>
        </Link>
        {session ? (
          <Link to="/me" data-testid="fitkey-page-me" className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[12px] hover:bg-neutral-50">
            <KeyRound className="h-3.5 w-3.5" /> My FitKey
          </Link>
        ) : (
          <Link
            to={`/signin?next=${encodeURIComponent(`/k/${fitkeyId}`)}`}
            data-testid="fitkey-page-signin"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[12px] hover:bg-neutral-50"
          >
            <KeyRound className="h-3.5 w-3.5" /> Sign in with FitKey
          </Link>
        )}
      </header>

      <div className="mx-auto max-w-xl px-5 pb-16">
        {/* Compact pass header (public data only) */}
        <div
          data-testid="fitkey-page-pass"
          className="relative rounded-[24px] bg-[#0A0A0A] text-white p-5 overflow-hidden ring-1 ring-inset ring-white/10 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)]"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -top-1/2 -left-1/4 h-[160%] w-[90%]"
            style={{ background: "radial-gradient(closest-side, rgba(255,255,255,0.10), transparent 70%)" }}
          />
          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-white/55">
                <span className="rounded-[8px] ring-1 ring-white/15">
                  <FittinMark size={22} bg="#141414" />
                </span>
                FitKey
              </div>
              <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-white/45">Your size</div>
              <div data-testid="fitkey-page-size" className="mt-0.5 flex items-baseline gap-1.5 leading-none tracking-[-0.04em] font-medium">
                <span className="text-[18px] text-white/55">{size.unit}</span>
                <span className={`text-[48px] ${size.value === "—" ? "text-white/25" : ""}`}>
                  {loadState === "loading" ? "··" : size.value}
                </span>
              </div>
              <div data-testid="fitkey-page-pair" className="mt-2 text-[13px] text-white/80 truncate">
                {loadState === "loading" ? "Loading your pass…" : pair || "Your pair"}
              </div>
              {summary?.pass_status && (
                <div data-testid="fitkey-page-status" className="mt-2 inline-flex rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] text-white/80">
                  {summary.pass_status}
                </div>
              )}
            </div>
            <div className="shrink-0 rounded-xl bg-white p-2">
              <PassQR seed={fitkeyId} size={64} />
            </div>
          </div>
          <div className="relative mt-4 flex items-center justify-between border-t border-white/10 pt-3 text-[10.5px] text-white/45">
            <span className="font-mono truncate" data-testid="fitkey-page-id">{fitkeyId}</span>
            {preview && <span className="shrink-0 ml-3">Preview</span>}
          </div>
        </div>

        {loadState === "notfound" && (
          <Notice testid="fitkey-page-notfound">
            We couldn’t find this FitKey. Check the link on your pass, or{" "}
            <Link to="/#create-fitkey" className="underline underline-offset-2">create a new one</Link>.
          </Notice>
        )}
        {loadState === "error" && (
          <Notice testid="fitkey-page-error">We couldn’t load your pass right now. Please try again in a moment.</Notice>
        )}

        {linkState === "signing" ? (
          <div data-testid="fitkey-page-signing" className="mt-6 flex items-center gap-2.5 rounded-2xl border border-black/10 bg-white p-5 text-[13px] text-neutral-600">
            <span className="h-3.5 w-3.5 rounded-full border-2 border-black/15 border-t-[#0A0A0A] animate-spin" />
            Signing you in with your FitKey…
          </div>
        ) : signedHere && loadState === "ok" ? (
          <>
            <section data-testid="fitkey-confirm" className="mt-6 rounded-2xl border border-black/10 bg-white p-5">
              <div className="flex items-center justify-between">
                <h1 className="text-[17px] font-medium tracking-tight">Confirm fit</h1>
                <span className="text-[10.5px] uppercase tracking-[0.2em] text-neutral-500">How did it fit?</span>
              </div>
              <ConfirmFitForm fitkeyId={fitkeyId} brand={brand} model={model} sizeRecommended={sizeRecommended} testid="fitkey-confirm" />
            </section>

            <section data-testid="fitkey-return" className="mt-4 rounded-2xl border border-black/10 bg-white p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-[17px] font-medium tracking-tight">Request return</h2>
                <Undo2 className="h-4 w-4 text-neutral-400" />
              </div>
              <ReturnForm fitkeyId={fitkeyId} testid="fitkey-return" />
            </section>
          </>
        ) : loadState === "ok" ? (
          <section data-testid="fitkey-signin-prompt" className="mt-6 rounded-2xl border border-black/10 bg-white p-5">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4" />
              <h1 className="text-[17px] font-medium tracking-tight">Sign in to request a return</h1>
            </div>
            <p className="mt-2 text-[13px] text-neutral-600 leading-relaxed">
              {linkState === "failed"
                ? "This link couldn’t sign you in. Open your FitKey in your Wallet and tap the link again, or enter a code from a signed-in device."
                : session
                ? "You’re signed in with a different FitKey on this device. Open this FitKey from your Wallet, or enter a code from a device where it’s signed in."
                : "Open your FitKey in your Wallet and tap the link, or enter a code from another device. Then you can confirm the fit or request a return here."}
            </p>
            <Link
              to={`/signin?next=${encodeURIComponent(`/k/${fitkeyId}`)}`}
              data-testid="fitkey-signin-cta"
              className="mt-4 group inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] text-white h-11 px-5 text-[13px] hover:bg-neutral-800 transition-all"
            >
              Sign in with FitKey
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </section>
        ) : null}

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3 text-[11.5px] text-neutral-500">
          <span>This link belongs to your FitKey. Keep it on your pass.</span>
          <span className="flex gap-4">
            <Link to="/legal/privacy" data-testid="fitkey-page-privacy" className="hover:text-[#0A0A0A]">Privacy</Link>
            <Link to="/legal/imprint" data-testid="fitkey-page-imprint" className="hover:text-[#0A0A0A]">Imprint</Link>
          </span>
        </div>
      </div>
    </main>
  );
}

function Notice({ children, testid }) {
  return (
    <div data-testid={testid} className="mt-6 rounded-2xl border border-black/10 bg-white p-5 text-[13px] text-neutral-600">
      {children}
    </div>
  );
}
